import { useFrame } from '@react-three/fiber'
import { useRef } from 'react'
import type { Group } from 'three'
import type { AvatarPresentation, MotionSequence } from '../types'
import { useApplyAvatarPresentationFrame } from './useApplyAvatarPresentationFrame'
import { useAvatarPresentationTargets } from './useAvatarPresentationTargets'
import { useResolvedAvatarPresentation } from './useResolvedAvatarPresentation'
import { useResponsiveAvatarPlacement } from './useResponsiveAvatarPlacement'
import { useSnapPositionOnViewportResize } from './useSnapPositionOnViewportResize'

const AVATAR_PRESENTATION_LERP_ALPHA = 0.1
const RESTART_AVATAR_PRESENTATION_LERP_ALPHA = 0.1

type UseAvatarPresentationParams = {
  avatarPresentation?: AvatarPresentation
  restartKey?: MotionSequence['restartKey']
}

/** アバターの位置や回転、拡大縮小を制御するフック */
export const useAvatarPresentation = ({ avatarPresentation, restartKey }: UseAvatarPresentationParams) => {
  const groupRef = useRef<Group>(null)
  const shouldSnapPositionRef = useSnapPositionOnViewportResize()
  const resolveResponsiveAvatarPlacement = useResponsiveAvatarPlacement({ avatarPresentation, groupRef })

  /** presentation 解決（再計算判定 + 初期ウォームアップ） */
  const { resolveForFrame } = useResolvedAvatarPresentation({
    avatarPresentation,
    restartKey,
    resolveResponsiveAvatarPlacement,
  })

  /** 位置/回転/拡大縮小のターゲット管理 */
  const { targetPositionRef, targetQuaternionRef, syncPositionTarget, getNextScale } = useAvatarPresentationTargets({
    avatarPresentation,
  })

  /** three.js group への最終適用 */
  const { applyFrame } = useApplyAvatarPresentationFrame({ groupRef })

  useFrame(() => {
    /** 1フレーム分の presentation を確定する */
    const { presentation, isInitialResolveFrame, isRestartTransitionFrame } = resolveForFrame(
      shouldSnapPositionRef.current
    )
    syncPositionTarget(presentation)

    /** 初期ウォームアップ中または resize 時は位置をスナップする */
    const shouldSnap = shouldSnapPositionRef.current || isInitialResolveFrame
    const lerpAlpha = isRestartTransitionFrame ? RESTART_AVATAR_PRESENTATION_LERP_ALPHA : AVATAR_PRESENTATION_LERP_ALPHA
    const currentScale = groupRef.current?.scale.x ?? 1
    const nextScale = getNextScale({ currentScale, presentation, lerpAlpha })
    const applied = applyFrame({
      targetPosition: targetPositionRef.current,
      targetQuaternion: targetQuaternionRef.current,
      nextScale,
      shouldSnap,
      lerpAlpha,
    })

    if (applied && shouldSnapPositionRef.current) {
      shouldSnapPositionRef.current = false
    }
  })

  return groupRef
}
